/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Modal from 'react-responsive-modal';
import { withTheme } from 'styled-components';
import { TextInput, Selectbox, Button } from '@01_people/ui-library';
import phoneData from 'libphonenumber-js/metadata.full.json';
import { withTranslation, useTranslation, Trans } from 'react-i18next';
// import i18n from '../../../locale';
import * as ActionCreators from '../../../redux/action';
import { validatePhoneNumber } from '../../../utils/common';
import AppLoader from '../../app-loader';
import SmsIcon from '../../../assets/svg/2fa-sms.svg';
import ChangePhoneNumberProcessSC from './change-phone-number-process.styled';
import SvgIcon from '../../svg-icon';

const FIRST_STEP = 1;
export const SECOND_STEP = 2;
export const THIRD_STEP = 3;
const FOURTH_STEP = 4;

const phonePrefixes = Object.keys(phoneData.countries).map(ctry => ({
  label: `${ctry} +${phoneData.countries[ctry][0]}`,
  value: `+${phoneData.countries[ctry][0]}`
}));

const StepOne = ({
  errors,
  handlePhonePrefixChange,
  handlePhoneValueChange,
  handlePhonePrefixBlur,
  handlePhoneValueBlur,
  phoneNumberVerified
}) => {
  const { t } = useTranslation('pages');
  return (
    <div className="change-process-modal">
      <h4 className="change-process-modal__heading">
        {t('profile.phone.modal.1.title')}
      </h4>
      <p className="change-process-modal__description">
        {phoneNumberVerified
          ? t('profile.phone.modal.1.description.verified')
          : t('profile.phone.modal.1.description.unverified')}
      </p>
      <div className="phone">
        <div className="phone__left">
          <Selectbox
            customClass="phone-prefix txt-inp"
            label={t('profile.phone.modal.1.prefixLabel')}
            placeholder={t('profile.phone.modal.1.prefixLabel')}
            onChange={handlePhonePrefixChange}
            onBlur={handlePhonePrefixBlur}
            options={phonePrefixes}
            noOptionsMessage={() => t('components:select.no_options')}
          />
        </div>

        <div className="phone__right">
          <TextInput
            type="text"
            name="phoneNumber"
            label={t('profile.phone.modal.1.phoneLabel')}
            customClass="phone-number txt-inp"
            onChange={handlePhoneValueChange}
            onBlur={handlePhoneValueBlur}
          />
        </div>
      </div>
      {errors.length > 0 ? (
        <div className="text-error">{`${errors}`}</div>
      ) : (
        <div className="mt-16" />
      )}
    </div>
  );
};

StepOne.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  handlePhonePrefixChange: PropTypes.func.isRequired,
  handlePhoneValueChange: PropTypes.func.isRequired,
  handlePhonePrefixBlur: PropTypes.func.isRequired,
  handlePhoneValueBlur: PropTypes.func.isRequired,
  phoneNumberVerified: PropTypes.bool
};

StepOne.defaultProps = {
  phoneNumberVerified: false
};

const StepTwo = ({
  currentPhoneNumber,
  errors,
  handleTextInputNumber,
  handleTextInputBlur
}) => {
  const { t } = useTranslation('pages');
  return (
    <div className="change-process-modal">
      <h4 className="change-process-modal__heading">
        {t('profile.phone.modal.2.title')}
      </h4>
      <p className="change-process-modal__description">
        <Trans
          i18nKey="profile.phone.modal.2.description"
          ns="pages"
          values={{ currentPhoneNumber }}
        />
      </p>
      <TextInput
        customClass="change-process-modal__input"
        label={t('profile.phone.modal.2.label', { currentPhoneNumber })}
        errors={errors}
        id="old-verification-code"
        onChange={handleTextInputNumber}
        onBlur={handleTextInputBlur}
      />
    </div>
  );
};

StepTwo.propTypes = {
  currentPhoneNumber: PropTypes.string.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleTextInputNumber: PropTypes.func.isRequired,
  handleTextInputBlur: PropTypes.func.isRequired
};

const StepThree = ({
  phoneNumber,
  errors,
  handleTextInputNumber,
  handleTextInputBlur
}) => {
  const { t } = useTranslation('pages');
  return (
    <div className="change-process-modal">
      <h4 className="change-process-modal__heading">
        {t('profile.phone.modal.3.title')}
      </h4>
      <p className="change-process-modal__description">
        <Trans
          i18nKey="profile.phone.modal.3.description"
          ns="pages"
          values={{ phoneNumber }}
        />
      </p>
      <TextInput
        customClass="change-process-modal__input"
        label={t('profile.phone.modal.3.label', { phoneNumber })}
        errors={errors}
        id="new-verification-code"
        defaultValue=""
        onChange={handleTextInputNumber}
        onBlur={handleTextInputBlur}
      />
    </div>
  );
};

StepThree.propTypes = {
  phoneNumber: PropTypes.string.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleTextInputNumber: PropTypes.func.isRequired,
  handleTextInputBlur: PropTypes.func.isRequired
};

const StepFour = () => {
  const { t } = useTranslation('pages');
  return (
    <div className="change-process-modal">
      <h4 className="change-process-modal__heading">
        {t('profile.phone.modal.4.title')}
      </h4>
      <p className="change-process-modal__description">
        {t('profile.phone.modal.4.description')}
      </p>
    </div>
  );
};

class ChangePhoneNumberProcess extends React.PureComponent {
  initialState = {
    errors: [],
    modalOpen: false,
    currentStep: null,
    newPhoneNumber: {
      valid: false,
      prefixInputFocused: false,
      valueInputFocused: false,
      prefixInputBlurred: false,
      valueInputBlurred: false,
      prefix: null,
      value: null
    },
    verificationCode: {
      valid: false,
      focused: false,
      blurred: false,
      value: null
    }
  };

  constructor(props) {
    super(props);
    this.state = this.initialState;
  }

  /**
   * Reset state & opens modal
   */
  handleChangePhoneBtnClick = () => {
    this.setState(state => ({
      ...state,
      modalOpen: true,
      errors: [],
      currentStep: FIRST_STEP,
      newPhoneNumber: {
        ...state.newPhoneNumber,
        prefixInputFocused: false,
        valueInputFocused: false,
        prefixInputBlurred: false,
        valueInputBlurred: false,
        valid: false,
        prefix: null,
        value: null
      }
    }));
  };

  phoneNumValidation = (callback = null) => {
    const {
      newPhoneNumber: { prefix, value, prefixInputBlurred, valueInputBlurred }
    } = this.state;

    const { currentPhoneNumber, t } = this.props;

    const emptyPrefix = !prefix || !prefix.length;

    const fullPhone = `${prefix} ${value}`;

    const phoneCheckValid = validatePhoneNumber(fullPhone);

    const phoneSameAsOld = currentPhoneNumber === fullPhone;
    const phoneValid = phoneCheckValid && !phoneSameAsOld && !emptyPrefix;

    let errors = [];

    let displayErrors = prefixInputBlurred && valueInputBlurred;

    if (!phoneValid) {
      errors.push(t('profile.phone.notValidPhone'));
    }

    if (emptyPrefix) {
      errors = [];
      errors.push(t('profile.phone.wrongPrefix'));
      displayErrors = true;
    }

    if (phoneSameAsOld) {
      errors = [];
      errors.push(t('profile.phone.samePhone'));
      displayErrors = true;
    }

    this.setState(
      state => ({
        ...state,
        errors: errors && displayErrors ? errors : [],
        newPhoneNumber: {
          ...state.newPhoneNumber,
          valid: phoneValid
        }
      }),
      callback !== null ? callback(errors) : null
    );
  };

  validateVerificationCode = (value, callback = null) => {
    const errors = [];
    const verificationCodeNotEmpty = !(!value || value === 0);
    const { t } = this.props;
    if (!verificationCodeNotEmpty) {
      errors.push(t('profile.phone.wrongCode'));
    }
    this.setState(
      state => ({
        ...state,
        errors,
        newPhoneNumber: {
          ...state.newPhoneNumber,
          valid: true,
          prefixInputFocused: false,
          valueInputFocused: false,
          prefixInputBlurred: false,
          valueInputBlurred: false
        },
        verificationCode: {
          valid: verificationCodeNotEmpty,
          focused: true,
          blurred: false,
          value
        }
      }),
      callback !== null ? callback(errors) : null
    );
  };

  handlePhonePrefixChange = value => {
    this.setState(
      state => ({
        ...state,
        newPhoneNumber: {
          ...state.newPhoneNumber,
          prefix: value ? value.value : '',
          prefixInputBlurred: true
        }
      }),
      this.phoneNumValidation
    );
  };

  handlePhoneValueChange = value => {
    this.setState(
      state => ({
        ...state,
        newPhoneNumber: {
          ...state.newPhoneNumber,
          value: value || ''
        }
      }),
      this.phoneNumValidation
    );
  };

  /**
   * Handler called as user types in new phone number
   */
  handleTextInputAddress = value => {
    const { currentStep } = this.state;
    switch (currentStep) {
      case FIRST_STEP:
        this.phoneNumValidation();
        break;
      case SECOND_STEP:
      case THIRD_STEP:
        this.validateVerificationCode(value);
        break;
      default:
        break;
    }
  };

  handleTextInputBlur = () => {
    const { verificationCode } = this.state;
    this.validateVerificationCode(verificationCode.value);
  };

  handlePhonePrefixBlur = () => {
    this.setState(
      state => ({
        ...state,
        newPhoneNumber: {
          ...state.newPhoneNumber,
          prefixInputBlurred: true
        }
      }),
      this.phoneNumValidation
    );
  };

  handlePhoneValueBlur = () => {
    this.setState(
      state => ({
        ...state,
        newPhoneNumber: {
          ...state.newPhoneNumber,
          valueInputBlurred: true
        }
      }),
      this.phoneNumValidation
    );
  };

  handleCloseModalClick = () => {
    this.setState(state => ({
      ...state,
      modalOpen: false
    }));
  };

  nextStep = () => {
    const {
      currentPhoneNumber,
      changePhoneNumber,
      verifyPhoneNumber,
      phoneNumberVerified,
      t,
      toggleInfoModal
    } = this.props;
    const { currentStep, newPhoneNumber, verificationCode } = this.state;

    // call efects when modal moves
    switch (currentStep) {
      case FIRST_STEP:
        // if sms verification is checked, we need to do multistep process. Otherwise we just change
        // the number in one step
        if (phoneNumberVerified) {
          changePhoneNumber(
            newPhoneNumber.prefix + newPhoneNumber.value,
            (errors, nextStep) =>
              this.setState(state => ({
                ...state,
                errors,
                currentStep: nextStep
              }))
          );
        } else {
          changePhoneNumber(
            newPhoneNumber.prefix + newPhoneNumber.value,
            () => {
              this.setState(this.initialState);
              toggleInfoModal({
                isOpen: true,
                type: 'success',
                message: t('profile.phone.modal.1.successChange'),
                callback: {
                  fnc: null,
                  btnText: null
                }
              });
            }
          );
        }
        break;
      case SECOND_STEP:
        verifyPhoneNumber(
          currentPhoneNumber,
          verificationCode.value,
          currentStep,
          (errors, nextStep) => {
            this.setState(state => ({
              ...state,
              errors,
              currentStep: nextStep
            }));
          }
        );
        break;
      case THIRD_STEP:
        verifyPhoneNumber(
          newPhoneNumber.prefix + newPhoneNumber.value,
          verificationCode.value,
          currentStep,
          (errors, nextStep) => {
            this.setState(state => ({
              ...state,
              errors,
              currentStep: nextStep,
              verificationCode: {
                valid: false,
                prefixInputFocused: false,
                blurred: false,
                value: null
              }
            }));
          }
        );
        break;
      default:
        break;
    }
  };

  handleKeyPress = e => {
    if (e.key === 'Enter') {
      const { currentStep, verificationCode } = this.state;
      switch (currentStep) {
        case FIRST_STEP:
          this.phoneNumValidation(errors =>
            errors.length <= 0 ? this.nextStep() : null
          );
          break;
        case SECOND_STEP:
        case THIRD_STEP:
          this.validateVerificationCode(verificationCode.value, errors =>
            errors.length <= 0 ? this.nextStep() : null
          );
          break;
        case FOURTH_STEP:
          this.handleOkButton();
          break;
        default:
          break;
      }
    }
  };

  handleOkButton = () => {
    const { updateUser, user } = this.props;
    const {
      newPhoneNumber: { prefix, value }
    } = this.state;

    updateUser({
      ...user,
      phone: {
        number: value,
        prefix: prefix.replace('+', '')
      }
    });
    this.handleCloseModalClick();
  };

  renderModalContent = () => {
    const { currentPhoneNumber, phoneNumberVerified } = this.props;
    const { currentStep, errors, newPhoneNumber } = this.state;
    switch (currentStep) {
      case FIRST_STEP:
        return (
          <StepOne
            errors={errors}
            handlePhonePrefixChange={this.handlePhonePrefixChange}
            handlePhoneValueChange={this.handlePhoneValueChange}
            handlePhonePrefixBlur={this.handlePhonePrefixBlur}
            handlePhoneValueBlur={this.handlePhoneValueBlur}
            phoneNumberVerified={phoneNumberVerified}
          />
        );
      case SECOND_STEP:
        return (
          <StepTwo
            currentPhoneNumber={currentPhoneNumber}
            errors={errors}
            handleTextInputNumber={this.handleTextInputAddress}
            handleTextInputBlur={this.handleTextInputBlur}
          />
        );
      case THIRD_STEP:
        return (
          <StepThree
            phoneNumber={newPhoneNumber.prefix + newPhoneNumber.value}
            errors={errors}
            handleTextInputNumber={this.handleTextInputAddress}
            handleTextInputBlur={this.handleTextInputBlur}
          />
        );
      case FOURTH_STEP:
        return <StepFour />;
      default:
        return (
          <StepOne
            errors={errors}
            handlePhonePrefixChange={this.handlePhonePrefixChange}
            handlePhoneValueChange={this.handlePhoneValueChange}
            handlePhonePrefixBlur={this.handlePhonePrefixBlur}
            handlePhoneValueBlur={this.handlePhoneValueBlur}
            phoneNumberVerificated={phoneNumberVerified}
          />
        );
    }
  };

  renderModal = () => {
    const { apiCallState, t } = this.props;

    const {
      modalOpen,
      currentStep,
      newPhoneNumber,
      verificationCode
    } = this.state;

    const buttonProps = {
      label:
        currentStep === FOURTH_STEP ? t('actions:finish') : t('actions:next'),
      onClick: currentStep === FOURTH_STEP ? this.handleOkButton : this.nextStep
    };

    if (currentStep === FIRST_STEP && !newPhoneNumber.valid)
      buttonProps.disabled = 'disabled';
    if (currentStep === SECOND_STEP && !verificationCode.valid)
      buttonProps.disabled = 'disabled';

    return (
      <Modal
        open={modalOpen}
        onClose={this.handleCloseModalClick}
        classNames={{
          modal: 'info-modal change-process',
          overlay: 'info-overlay',
          closeButton: `info-modal__close-btn${
            apiCallState === 'start' ? '--hidden' : ''
          }`
        }}
      >
        <svg className="blur-svg">
          <defs>
            <filter id="blur-filter">
              <feGaussianBlur stdDeviation="3" />
            </filter>
          </defs>
        </svg>
        <div
          className={apiCallState === 'start' ? 'info-modal--blurred' : null}
          onKeyPress={this.handleKeyPress}
          role="presentation"
        >
          <SvgIcon className="sms-icon-change" icon={SmsIcon} themed active />

          {this.renderModalContent()}
          <Button {...buttonProps} />
        </div>
        {apiCallState === 'start' ? <AppLoader size="modal" /> : null}
      </Modal>
    );
  };

  render() {
    const { currentPhoneNumber, t, nonEditableFields } = this.props;
    return (
      <ChangePhoneNumberProcessSC>
        <div className="input-wrapper">
          <div className="input-wrapper__left">
            <h6>{t('profile.phone.label')}</h6>
          </div>
          <div className="input-wrapper__right">
            <TextInput
              label={t('profile.phone.label')}
              customClass="txt-inp"
              defaultValue={currentPhoneNumber}
              disabled
            />
          </div>
        </div>
        <div className="button-wrapper">
          <Button
            styling="secondary"
            label={t('profile.phone.changeAction')}
            onClick={this.handleChangePhoneBtnClick}
            disabled={nonEditableFields.includes('phoneNumber')}
          />
        </div>
        {this.renderModal()}
      </ChangePhoneNumberProcessSC>
    );
  }
}

ChangePhoneNumberProcess.propTypes = {
  apiCallState: PropTypes.string.isRequired,
  currentPhoneNumber: PropTypes.string.isRequired,
  phoneNumberVerified: PropTypes.bool.isRequired,
  changePhoneNumber: PropTypes.func.isRequired,
  verifyPhoneNumber: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  toggleInfoModal: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  user: PropTypes.shape({}).isRequired,
  nonEditableFields: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      changePhoneNumber: ActionCreators.changePhoneNumber,
      verifyPhoneNumber: ActionCreators.verifyPhoneNumber,
      toggleInfoModal: ActionCreators.toggleInfoModal,
      updateUser: ActionCreators.updateUser
    },
    dispatch
  );

export default withTranslation('pages')(
  connect(
    state => ({
      apiCallState: state.ui.phoneChangeProcess.apiCallState,
      currentPhoneNumber: `+${state.user.phone.prefix || ''} ${state.user.phone
        .number || ''}`,
      phoneNumberVerified: state.user.verifications.phone_number,
      user: state.user,
      nonEditableFields: state.user.nonEditableFields,
    }),
    mapDispatchToProps
  )(withTheme(ChangePhoneNumberProcess))
);
